import axios from 'axios';

const httpProvider = axios.create({
    baseURL: `${process.env.REACT_APP_API_HOST}:${process.env.REACT_APP_API_HOST_PORT}`,
});

// Attach the auth token at request time, read directly from localStorage.
// This is intentionally NOT wired up via a React useEffect in AuthProvider:
// on initial mount, React fires child components' effects (e.g. a page's
// data-fetch-on-mount effect) before a parent provider's effect, so an
// interceptor registered in AuthProvider's useEffect could miss the very
// first request(s) after a page load/refresh, sending them unauthenticated
// and causing a spurious 401 even though the user has a valid token.
// Reading the token here, synchronously per request, avoids that race.
httpProvider.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = token;
        }
        return config;
    },
    error => Promise.reject(error)
);

httpProvider.interceptors.response.use(
    response => {
        // The success handler already returns response.data, which is good.
        return response.data;
    },
    error => {
        if (error.response?.status === 401) {
            const { pathname, search, hash } = window.location;
            const onAuthPage =
                pathname.startsWith('/login') || pathname.startsWith('/logout');
            const target = onAuthPage
                ? '/logout'
                : `/logout?from=${encodeURIComponent(`${pathname}${search}${hash}`)}`;
            window.location.replace(target);
            return Promise.reject(error); // Ensure we still reject after redirecting
        }

        // Centralized error message parsing
        let message = 'An error occurred while processing your request.';
        const errorData = error.response?.data;

        if (errorData) {
            if (typeof errorData === 'object' && errorData.error) {
                // Standard JSON error object: { "error": "message" }
                message = errorData.error;
            } else if (typeof errorData === 'string') {
                // Handle plain string or concatenated JSON string
                try {
                    // Attempt to parse the first part of the string as JSON
                    const endOfFirstJson = errorData.indexOf('}') + 1;
                    if (endOfFirstJson > 0) {
                        const firstJsonString = errorData.substring(
                            0,
                            endOfFirstJson
                        );
                        const parsed = JSON.parse(firstJsonString);
                        message = parsed.error || errorData; // Use parsed error or fall back to the full string
                    } else {
                        message = errorData; // It's just a plain string
                    }
                } catch {
                    // If parsing fails, use the raw string as the message
                    message = errorData;
                }
            }
        } else if (error.request) {
            // The request was made but no response was received
            message =
                'Could not connect to the server. Please check your network connection.';
        } else {
            // Something happened in setting up the request that triggered an Error
            message = error.message;
        }

        // Attach the parsed message to the error object for easy access in components
        error.message = message;

        return Promise.reject(error);
    }
);
export default httpProvider;
